import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { useResendVerification, useVerifyEmail } from '@/features/auth/hooks/useAuth'
import { otpCodeSchema } from '@/features/auth/schemas'
import { AuthLayout } from '@/layouts/AuthLayout'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z.object({
  email: z.email('Enter a valid email address'),
  code: otpCodeSchema,
})

type FormValues = z.infer<typeof schema>

export function VerifyEmailPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const prefillEmail = (location.state as { email?: string } | null)?.email ?? ''
  const verifyEmail = useVerifyEmail()
  const resend = useResendVerification()
  const [apiError, setApiError] = useState<unknown>(null)
  const [resendMessage, setResendMessage] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    getValues,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: prefillEmail, code: '' },
  })

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    verifyEmail.mutate(values, {
      onSuccess: () => void navigate('/login', { replace: true }),
      onError: (error) => setApiError(error),
    })
  })

  const handleResend = () => {
    const email = getValues('email')
    if (!email) {
      setError('email', { message: 'Enter your email above first.' })
      return
    }
    setApiError(null)
    setResendMessage(null)
    resend.mutate(
      { email },
      {
        onSuccess: () => setResendMessage('A new code has been sent to your email.'),
        onError: (error) => setApiError(error),
      },
    )
  }

  return (
    <AuthLayout title="Verify Your Email" subtitle="Enter the 6-digit code we emailed you">
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(apiError)}
        generalMessage={apiError ? getErrorMessage(apiError) : null}
      />
      {resendMessage && (
        <div className="alert alert-success" role="alert">
          {resendMessage}
        </div>
      )}
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <FormInput control={control} name="email" label="Email" type="email" required />
        <FormInput control={control} name="code" label="Verification Code" required />
        <Button type="submit" className="w-100 mb-2" loading={verifyEmail.isPending}>
          Verify Email
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="w-100"
          loading={resend.isPending}
          onClick={handleResend}
        >
          Resend Code
        </Button>
      </form>
      <div className="text-center mt-3">
        <p className="text-muted mb-0">
          <Link to="/login" className="text-primary">
            Back to Sign In
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}
